/**
 * BAMF Chancenkarte (Opportunity Card) points logic.
 * Official threshold: 6 points to qualify.
 * Source: §20a AufenthG + BAMF scoring table.
 */

export type QualificationLevel = 'recognized_degree' | 'unrecognized_degree' | 'vocational' | 'none';
export type GermanLevel = 'none' | 'a1' | 'a2' | 'b1' | 'b2' | 'c1_plus';
export type EnglishLevel = 'none' | 'a1_b1' | 'b2_plus';
export type ExperienceLevel = 'none' | 'lt3' | 'y3_5' | 'y5_plus';
export type AgeGroup = 'under35' | 'y35_40' | 'over40';

export interface ChancenkarteInputs {
  qualification: QualificationLevel;
  germanLevel: GermanLevel;
  englishLevel: EnglishLevel;
  experience: ExperienceLevel;
  age: AgeGroup;
  previousGermanyStay: boolean;
}

export interface PointBreakdown {
  qualification: number;
  german: number;
  english: number;
  experience: number;
  age: number;
  germanyStay: number;
  total: number;
}

export const QUALIFY_THRESHOLD = 6;
export const MAX_POINTS = 13;

export function calculatePoints(inputs: ChancenkarteInputs): PointBreakdown {
  const qualification =
    inputs.qualification === 'recognized_degree' ? 4
    : inputs.qualification === 'unrecognized_degree' ? 2
    : inputs.qualification === 'vocational' ? 2
    : 0;

  const german =
    inputs.germanLevel === 'c1_plus' ? 3
    : inputs.germanLevel === 'b2' ? 2
    : inputs.germanLevel === 'b1' ? 1
    : 0;

  const english =
    inputs.englishLevel === 'b2_plus' ? 1 : 0;

  const experience =
    inputs.experience === 'y5_plus' ? 2
    : inputs.experience === 'y3_5' ? 1
    : 0;

  const age =
    inputs.age === 'under35' ? 2
    : inputs.age === 'y35_40' ? 1
    : 0;

  const germanyStay = inputs.previousGermanyStay ? 1 : 0;

  const total = qualification + german + english + experience + age + germanyStay;

  return { qualification, german, english, experience, age, germanyStay, total };
}

export function getScoreLabel(total: number): { label: string; tier: 'qualifying' | 'borderline' | 'notyet' } {
  if (total >= 8) return { label: 'Strong qualifier', tier: 'qualifying' };
  if (total >= QUALIFY_THRESHOLD) return { label: 'Qualifies', tier: 'qualifying' };
  if (total === 5) return { label: 'One point away', tier: 'borderline' };
  return { label: 'Not yet qualifying', tier: 'notyet' };
}

export function getBoostTip(inputs: ChancenkarteInputs, breakdown: PointBreakdown): string | null {
  if (breakdown.total >= QUALIFY_THRESHOLD) return null;
  const gap = QUALIFY_THRESHOLD - breakdown.total;

  // Prioritise easiest gains first
  if (breakdown.german < 3) {
    const germanGain = 3 - breakdown.german;
    if (germanGain >= gap) {
      const nextLevel = breakdown.german === 0 ? 'B1' : breakdown.german === 1 ? 'B2' : 'C1';
      return `Improving your German to ${nextLevel} would add ${germanGain > gap ? gap : germanGain} more point${gap > 1 ? 's' : ''} — enough to qualify.`;
    }
  }
  if (breakdown.english === 0) {
    return 'Reaching English B2 adds 1 point. Combined with other improvements this could get you over the line.';
  }
  if (breakdown.experience === 0) {
    return 'Gaining 3 years of relevant work experience adds 1 point toward the threshold.';
  }
  if (breakdown.qualification < 4 && inputs.qualification !== 'recognized_degree') {
    return 'Getting your foreign qualification officially recognised in Germany adds up to 2 extra points.';
  }
  return `You need ${gap} more point${gap > 1 ? 's' : ''} to qualify. Consider improving your German language level — it's the fastest path to extra points.`;
}
